import { quantityPricePoint } from './ticketPricePoints';

export const PHASE_PUBLIC = 'public';
export const PHASE_DAY_OF = 'day_of';
export const PHASE_PRESALE = 'presale';

export const DAY_OF_NAME = 'Day of Show';

function activePricePoint(pricePoints, now) {
  return pricePoints
    .filter((pricePoint) => pricePoint.starts_at && new Date(pricePoint.starts_at) <= now)
    .reduce((latest, candidate) => {
      if (!latest) return candidate;
      const latestStartsAt = new Date(latest.starts_at);
      const candidateStartsAt = new Date(candidate.starts_at);
      if (candidateStartsAt > latestStartsAt) return candidate;
      if (candidateStartsAt < latestStartsAt) return latest;
      return candidate.id > latest.id ? candidate : latest;
    }, null);
}

export function priceInEffectNow(ticketType, now = new Date()) {
  if (ticketType.free) return 'FREE';
  if (ticketType.price != null) return ticketType.price;

  const pricePoints = ticketType.price_points || [];
  const active = activePricePoint(pricePoints, now);
  if (active) return active.price_two_decimals ?? active.price;
  return ticketType.base_price_two_decimals ?? ticketType.base_price;
}

export function phasePrices(ticketType) {
  const base = ticketType.base_price_two_decimals ?? ticketType.base_price;
  const pointPrices = (ticketType.price_points || []).map((pricePoint) => pricePoint.price_two_decimals ?? pricePoint.price);

  return [base, ...pointPrices]
    .map((value) => (value == null ? null : parseFloat(value)))
    .filter((value) => value != null && !Number.isNaN(value));
}

export function priceRangeBounds(ticketType) {
  const prices = phasePrices(ticketType);
  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export const dayOfBoundaryFor = (confirm) => (
  (confirm && (confirm.door_time || confirm.start_time || confirm.end_time)) || null
);

function findDayOfPricePoint(pricePoints, dayOfBoundary) {
  const named = pricePoints.find((pricePoint) => pricePoint.name === DAY_OF_NAME);
  if (named) return named;

  if (!dayOfBoundary) return null;

  const boundary = new Date(dayOfBoundary).getTime();
  const onEventDay = pricePoints.filter(
    (pricePoint) => pricePoint.starts_at && new Date(pricePoint.starts_at).getTime() >= boundary
  );
  if (onEventDay.length === 0) return null;

  return onEventDay.reduce((latest, candidate) => (
    new Date(candidate.starts_at) > new Date(latest.starts_at) ? candidate : latest
  ));
}

export function ticketTypeToPhases(ticketType, dayOfBoundary = null) {
  const pricePoints = (ticketType.price_points || []).filter((pricePoint) => !pricePoint._destroy);
  const dayOfPricePoint = findDayOfPricePoint(pricePoints.filter((pricePoint) => !quantityPricePoint(pricePoint)), dayOfBoundary);
  const presalePricePoints = pricePoints.filter((pricePoint) => pricePoint !== dayOfPricePoint);

  return {
    [PHASE_PUBLIC]: {
      enabled: true,
      price: null,
      startShowActivityId: ticketType.start_show_activity_id,
      endShowActivityId: ticketType.end_show_activity_id,
    },
    dayOf: {
      enabled: Boolean(dayOfPricePoint),
      id: dayOfPricePoint ? dayOfPricePoint.id : undefined,
      newRecord: dayOfPricePoint ? dayOfPricePoint.newRecord : undefined,
      price: dayOfPricePoint ? (dayOfPricePoint.price_two_decimals ?? dayOfPricePoint.price) : null,
      startsAt: dayOfPricePoint ? dayOfPricePoint.starts_at : null,
      name: dayOfPricePoint ? dayOfPricePoint.name : DAY_OF_NAME,
    },
    [PHASE_PRESALE]: {
      enabled: presalePricePoints.length > 0,
      pricePoints: presalePricePoints,
    },
  };
}

export function phasesToTicketType(ticketType, phases) {
  const dayOf = phases.dayOf || {};
  const basePrice = ticketType.base_price_two_decimals ?? ticketType.base_price;
  const dayOfIsExisting = Boolean(dayOf.id && !dayOf.newRecord);
  let dayOfPricePoints = [];
  if (dayOf.enabled) {
    dayOfPricePoints = [{
      id: dayOf.id,
      newRecord: dayOf.newRecord,
      name: dayOfIsExisting ? dayOf.name : (dayOf.name || DAY_OF_NAME),
      starts_at: dayOf.startsAt,
      price_two_decimals: dayOf.price != null ? dayOf.price : basePrice,
    }];
  } else if (dayOfIsExisting) {
    dayOfPricePoints = [{
      id: dayOf.id,
      name: dayOf.name,
      starts_at: dayOf.startsAt,
      price_two_decimals: dayOf.price,
      _destroy: true,
    }];
  }
  const presalePricePoints = (phases[PHASE_PRESALE] && phases[PHASE_PRESALE].pricePoints) || [];

  const rebuilt = [...dayOfPricePoints, ...presalePricePoints];
  const rebuiltIds = new Set(rebuilt.filter((pricePoint) => pricePoint.id).map((pricePoint) => pricePoint.id));
  const preservedDestroys = (ticketType.price_points || [])
    .filter((pricePoint) => pricePoint._destroy && pricePoint.id && !rebuiltIds.has(pricePoint.id));

  return Object.assign({}, ticketType, {
    start_show_activity_id: phases.public.startShowActivityId,
    end_show_activity_id: phases.public.endShowActivityId,
    price_points: [...rebuilt, ...preservedDestroys],
  });
}

